import {
    Integration,
    IntegrationCompany,
    IntegrationReservationStatusLabel,
    integrationsByCompany,
    LinkType,
} from "../../Everou/_modelsTS/Integration";
import { IntegrationApp } from "../../Everou/_models/IntegrationApp";
import { useTranslation } from "react-i18next";
import {
    ContentSpace,
    CustomCard,
    MainPageWithButtons,
} from "../../Everou/_components";
import { PMSHostifyIntegration } from "./platforms/PMSHostifyIntegration";
import { PMSKrossBookingIntegration } from "./platforms/PMSKrossBookingIntegration";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { integrationsActions } from "../../Everou/_actions/integrations.actions";
import { useMemo } from "react";
import { IntegrationStore } from "../../Everou/_stores/IntegrationStore";
import { ReadOnlyDataRow } from "../../Everou/_components";
import { IntegrationsColumn } from "../../Everou/IntegrationsModule/components/IntegrationsColumn";
import { PMSSmoobuIntegration } from "./platforms/PMSSmoobuIntegration";
import { PMSIntegrationsActions } from "../_actions/PMSIntegrationsActions";
import { PMSAvantioIntegration } from "./platforms/PMSAvantioIntegration";
import { PMSOctorateIntegration } from "./platforms/PMSOctorateIntegration";
import { PMSAvaibookNewIntegration } from "./platforms/PMSAvaibookNewIntegration";
import { PMSLodgifyIntegration } from "./platforms/PMSLodgifyIntegration";
import { PMSGuestyIntegration } from "./platforms/PMSGuestyIntegration";
import { PMSThreeAngularIntegration } from "./platforms/PMSThreeAngularIntegration";
import { PMSIcneaIntegration } from "./platforms/PMSIcneaIntegration";
import { PMSBookiproIntegration } from "./platforms/PMSBookiproIntegration";
import { PMSEnsoConnectIntegration } from "./platforms/PMSEnsoConnectIntegration";
import { PMSManualIntegration } from "./platforms/PMSManualIntegration";
import { CapsuleButton } from "../../Common/_components";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { PMSOrganizationScopeSelector } from "../_components/PMSOrganizationScopeSelector";
import { PMSFocusedTemplateStore } from "../_stores/PMSFocusedTemplateStore";
import { PMSAccountDraftStore } from "../_stores/PMSAccountDraftStore";
import { useActiveOrganization } from "../_helpers/useActiveOrganization";
import { PMSAccountScopeInfoBanner } from "./_components/PMSAccountScopeInfoBanner";

type Props = {
    // Mounted inside another page (the organization's Integrations tab or a
    // location's integration setup): that page already names the scope and
    // draws the card, so the selector and the frame are dropped.
    isEmbedded?: boolean;
};

// The panel of a company of the catalog.
function companyPanel(secondaryView: IntegrationCompany | null) {
    switch (secondaryView) {
        case IntegrationCompany.AVANTIO:
            return <PMSAvantioIntegration />;

        case IntegrationCompany.AVAIBOOK:
            return <PMSAvaibookNewIntegration />;

        case IntegrationCompany.HOSTIFY:
            return <PMSHostifyIntegration />;

        case IntegrationCompany.KROSS_BOOKING:
            return <PMSKrossBookingIntegration />;

        case IntegrationCompany.THREE_ANGULAR:
            return <PMSThreeAngularIntegration />;

        case IntegrationCompany.OCTORATE:
            return <PMSOctorateIntegration />;

        case IntegrationCompany.SMOOBU:
            return <PMSSmoobuIntegration />;

        case IntegrationCompany.LODGIFY:
            return <PMSLodgifyIntegration />;

        case IntegrationCompany.GUESTY:
            return <PMSGuestyIntegration />;

        case IntegrationCompany.ICNEA:
            return <PMSIcneaIntegration />;

        case IntegrationCompany.BOOKIPRO:
            return <PMSBookiproIntegration />;

        case IntegrationCompany.ENSO_CONNECT:
            return <PMSEnsoConnectIntegration />;

        case IntegrationCompany.MANUAL:
            return <PMSManualIntegration />;
    }
    return null;
}
export function PMSIntegrationsModule({ isEmbedded = false }: Props = {}) {
    // INITIALIZATION
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const integrations = useSelector(Selectors.getPMSIntegrations);
    const avaibookIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.AVAIBOOK,
    );
    const hostifyIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.HOSTIFY,
    );
    const octorateIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.OCTORATE,
    );
    const krossBookingIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.KROSS_BOOKING,
    );
    const threeAngularIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.THREE_ANGULAR,
    );
    const smoobuIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.SMOOBU,
    );
    const avantioIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.AVANTIO,
    );
    const lodgifyIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.LODGIFY,
    );
    const guestyIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.GUESTY,
    );
    const icneaIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.ICNEA,
    );
    const bookiproIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.BOOKIPRO,
    );
    const ensoConnectIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.ENSO_CONNECT,
    );
    const manualIntegrations = integrationsByCompany(
        integrations,
        IntegrationCompany.MANUAL,
    );

    const currentIntegrationApps = [
        IntegrationApp({
            id: 0,
            company: IntegrationCompany.AVAIBOOK,
            link: LinkType.ICAL,
            installed: avaibookIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 1,
            company: IntegrationCompany.HOSTIFY,
            link: LinkType.WEBHOOK,
            installed: hostifyIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 2,
            company: IntegrationCompany.OCTORATE,
            link: LinkType.WEBHOOK,
            installed: octorateIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 3,
            company: IntegrationCompany.KROSS_BOOKING,
            link: LinkType.API_ENDPOINT,
            installed: krossBookingIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 4,
            company: IntegrationCompany.THREE_ANGULAR,
            link: LinkType.DEVELOPERS,
            installed: threeAngularIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 5,
            company: IntegrationCompany.SMOOBU,
            link: LinkType.WEBHOOK,
            installed: smoobuIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 6,
            company: IntegrationCompany.AVANTIO,
            link: LinkType.API_ENDPOINT,
            installed: avantioIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 7,
            company: IntegrationCompany.LODGIFY,
            link: LinkType.WEBHOOK,
            installed: lodgifyIntegrations[0].uid,
        }),
        // Guesty is hidden until it can actually import reservations: the backend
        // authenticates and lists properties, but has no reservation model, parser
        // nor webhook, so an integration set up here would never create a single
        // invitation. Nobody has one in production. Restore this entry (and its
        // page) once the sync exists.
        ...(guestyIntegrations[0].uid
            ? [IntegrationApp({
                id: 8,
                company: IntegrationCompany.GUESTY,
                link: LinkType.WEBHOOK,
                installed: guestyIntegrations[0].uid,
            })]
            : []),
        IntegrationApp({
            id: 9,
            company: IntegrationCompany.ICNEA,
            link: LinkType.DEVELOPERS,
            installed: icneaIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 10,
            company: IntegrationCompany.BOOKIPRO,
            link: LinkType.DEVELOPERS,
            installed: bookiproIntegrations[0].uid,
        }),
        IntegrationApp({
            id: 11,
            company: IntegrationCompany.ENSO_CONNECT,
            link: LinkType.WEBHOOK,
            installed: ensoConnectIntegrations[0].uid,
        }),
        // No PMS behind it: the manager creates the reservations by hand. Kept
        // in the same catalog because it is set up exactly like the others --
        // activate the account here, pick the rooms in the location.
        IntegrationApp({
            id: 12,
            company: IntegrationCompany.MANUAL,
            link: LinkType.LINK,
            installed: manualIntegrations[0].uid,
        }),
    ];

    const secondaryView = useSelector(
        Selectors.getIntegrationsModuleSecondaryView,
    );
    const selectedIntegration: Integration = useSelector(
        IntegrationStore.selectorGetSelectedItem,
    );

    const closeViewFn = useCallback(() => {
        dispatch(integrationsActions.changeSecondaryView(null));
        dispatch(IntegrationStore.actionSelectItem(null));
        dispatch(PMSFocusedTemplateStore.actionSet(null));
        dispatch(PMSAccountDraftStore.actionSet(null));
    }, [dispatch]);

    // The account this panel was opened for, if it was opened from an
    // integration (PMSNavigationActions.openIntegrationCompany). Only meant
    // for the company it names: picking another company from the catalog
    // browses that one whole.
    const focusedTemplate = useSelector(Selectors.getPMSFocusedTemplate);
    const focusApplies = !!focusedTemplate && focusedTemplate.company === secondaryView;
    const focusedInCatalog = focusApplies
        && integrations.some(({ uid }) => uid === focusedTemplate.uid);
    const showWholeCatalog = useCallback(() => {
        dispatch(PMSFocusedTemplateStore.actionSet(null));
    }, [dispatch]);
    // Picking another company from the catalog is browsing that one: the
    // account and draft the panel was opened for stay behind with their own
    // company, or coming back to it would bring them back unasked.
    const accountDraft = useSelector(Selectors.getPMSAccountDraft);
    const onSelectCompany = useCallback((company: string) => {
        if (focusedTemplate && focusedTemplate.company !== company)
            dispatch(PMSFocusedTemplateStore.actionSet(null));
        if (accountDraft && accountDraft.company !== company)
            dispatch(PMSAccountDraftStore.actionSet(null));
    }, [dispatch, focusedTemplate, accountDraft]);

    // Organization-centric (BAC-215): the list/create actions resolve the
    // active organization from the store, so it must be loaded first, and
    // the list refetches when the picker changes it.
    const { activeOrganization, isResolving, hasFailed } =
        useActiveOrganization();
    const previousScopeUid = useRef<string | null | undefined>(undefined);
    // Whether the current scope's list has landed: until then an account
    // missing from it may just not be loaded yet, not someone else's.
    const [catalogLoaded, setCatalogLoaded] = useState(false);

    useEffect(() => {
        if (isResolving) return;
        const scopeUid = activeOrganization?.uid ?? null;
        if (
            previousScopeUid.current !== undefined &&
            previousScopeUid.current !== scopeUid
        ) {
            // Scope switch: close any open form/detail (it belonged to the
            // previous organization) and drop its list so it can't linger
            // under the new scope if the refetch fails.
            closeViewFn();
            dispatch(PMSIntegrationsActions.listIntegrationsSuccess([]));
        }
        previousScopeUid.current = scopeUid;
        let cancelled = false;
        setCatalogLoaded(false);
        Promise.resolve(dispatch(PMSIntegrationsActions.listIntegrations()))
            .catch(() => undefined)
            .then(() => {
                if (!cancelled) setCatalogLoaded(true);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isResolving, activeOrganization?.uid]);

    useEffect(() => {
        return () => {
            closeViewFn();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const detailedContent = useMemo(() => {
        if (!selectedIntegration) {
            const panel = companyPanel(secondaryView);
            if (!panel || !focusApplies) return panel;
            return (
                <div className="c-h-100 d-flex flex-column">
                    {focusBanner()}
                    <div className="flex-grow-1">{panel}</div>
                </div>
            );
        }

        if (selectedIntegration.reservations?.length === 0) {
            return (
                <MainPageWithButtons
                    //dataTestId={testIDs.root}
                    pageTitle={t("mod_integrations_reservations_title")}
                    content={
                        <div className="c-h-100 d-flex justify-content-center align-items-center">
                            {t("mod_integrations_reservations_not_yet")}
                        </div>
                    }
                    buttons={undefined}
                />
            );
        }

        return (
            <MainPageWithButtons
                //dataTestId={testIDs.root}
                pageTitle={t("mod_integrations_reservations_title")}
                content={
                    <div className="py-4 c-grid">
                        {selectedIntegration.reservations?.map(
                            (reservation, index) => (
                                <CustomCard
                                    banner={
                                        IntegrationReservationStatusLabel[
                                            reservation.status
                                        ]
                                    }
                                    key={reservation.uid}
                                >
                                    <ReadOnlyDataRow
                                        key={reservation.uid + "1"}
                                        name={t("global_email")}
                                        data={reservation.email}
                                    />
                                    <ReadOnlyDataRow
                                        key={reservation.uid + "2"}
                                        name={t(
                                            "mod_integrations_reservation_id",
                                        )}
                                        data={reservation.reservationId}
                                    />

                                    <ReadOnlyDataRow
                                        key={reservation.uid + "3"}
                                        name={t(
                                            "mod_integrations_reservation_start",
                                        )}
                                        data={reservation.reservationStart}
                                    />

                                    <ReadOnlyDataRow
                                        key={reservation.uid + "4"}
                                        hasSeparators={false}
                                        name={t(
                                            "mod_integrations_reservation_end",
                                        )}
                                        data={reservation.reservationEnd}
                                    />
                                </CustomCard>
                            ),
                        )}
                    </div>
                }
                buttons={undefined}
            />
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondaryView, selectedIntegration?.reservations, focusApplies, focusedInCatalog, focusedTemplate, catalogLoaded]);


    function focusBanner() {
        const name = focusedTemplate.name || focusedTemplate.uid;
        if (!focusedInCatalog) {
            // Not in the catalog yet while it loads: say nothing rather than
            // flash the warning for an account that is about to appear.
            if (!catalogLoaded) return null;
            // Another user's personal account or another organization's:
            // not listable in this scope.
            return (
                <div className="alert alert-warning small mb-2">
                    {t("mod_pms_account_focus_foreign", { name })}
                </div>
            );
        }
        return (
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2" style={{ gap: "8px" }}>
                <span className="small text-muted">
                    {t("mod_pms_account_focus_title", { name })}
                </span>
                <CapsuleButton
                    size="SMALL"
                    textUppercase={false}
                    onClick={showWholeCatalog}
                    text={t("mod_pms_account_focus_show_all")}
                />
            </div>
        );
    }

    return (
        <ContentSpace
            isEmbedded={isEmbedded}
            mainContent={
                <MainPageWithButtons
                    content={integrationsContent()}
                    pageTitle={isEmbedded ? undefined : t("mod_pms_accounts_title")}
                    isFlat={isEmbedded}
                />
            }
            onGoBack={closeViewFn}
            detailedContent={detailedContent}
        />
    );

    function integrationsContent() {
        // A failed organizations load leaves listIntegrations without a
        // scope (it deliberately does not fall back to the personal
        // templates): say so with a retry instead of an empty catalog, like
        // PMSOrganizationDetail does.
        if (isResolving && hasFailed) return loadError();
        return (
            <div>
                {!isEmbedded && (
                    <PMSOrganizationScopeSelector
                        activeOrganization={activeOrganization}
                    />
                )}
                <div className="mb-3">
                    <PMSAccountScopeInfoBanner />
                </div>
                <div className="c-default-grid">
                    <IntegrationsColumn integrations={currentIntegrationApps} onSelect={onSelectCompany} />
                </div>
            </div>
        );
    }

    function loadError() {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-4">
                <h5 className="text-center">{t("global_error_network")}</h5>
                <div className="mt-3">
                    <CapsuleButton
                        text={t("global_retry")}
                        onClick={() =>
                            dispatch(OrganizationsActions.listOrganizations())
                        }
                    />
                </div>
            </div>
        );
    }
}
